/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { stringify } from 'qs'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { fetchAgendaEvents } from '../../lib/rest/agendaAPIClient'
import { getHalEmbedded } from '@jalik/hal'
import { useQuery } from '@tanstack/react-query'
import useParsedQueryParam from '../../../../client/js/hooks/useParsedQueryParam'
import { parseNumber } from '@jalik/form-parser'
import { Button, Card, Container, Stack } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import { faCalendarDay } from '@fortawesome/free-solid-svg-icons/faCalendarDay'
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight'
import { faRefresh } from '@fortawesome/free-solid-svg-icons/faRefresh'
import Agenda from './Agenda'
import Toolbar from '../../../../client/js/components/Toolbar'
import Page from '../../../../client/js/components/Page'
import PageTitle from '../../../../client/js/components/PageTitle'

function AgendaPage () {
  const intl = useIntl()

  const year = useParsedQueryParam('year', parseNumber)
  const month = useParsedQueryParam('month', parseNumber)

  const dateTime = useMemo(() => (
    year > 0 && month > 0
      ? DateTime.fromObject({ year, month })
      : DateTime.now()
  ), [month, year])

  const eventsQuery = useQuery({
    enabled: dateTime?.isValid === true,
    queryKey: ['agendaEvents', dateTime],
    queryFn: () => fetchAgendaEvents({
      startDate: dateTime.startOf('month').startOf('week').toISO(),
      endDate: dateTime.endOf('month').endOf('week').toISO()
    })
  })

  const events = useMemo(() => (
    ((eventsQuery.data != null ? getHalEmbedded(eventsQuery.data)?.events : null) || [])
  ), [eventsQuery.data])

  const nextMonth = useMemo(() => dateTime.plus({ month: 1 }), [dateTime])
  const previousMonth = useMemo(() => dateTime.minus({ month: 1 }), [dateTime])
  const today = useMemo(() => DateTime.now(), [])

  const handleRefreshClick = useCallback(() => {
    eventsQuery.refetch()
  }, [eventsQuery])

  const title = intl.formatMessage({
    id: 'agenda',
    defaultMessage: 'Agenda'
  })

  return (
    <Page>
      <Container size="xl">
        <PageTitle
          hidden
          title={title}
        />

        <Card shadow="sm" mih={800}>
          <Stack style={{ flexGrow: 1 }}>
            <Toolbar>
              <Button
                disabled={dateTime.hasSame(DateTime.now(), 'month')}
                component={AnchorLink}
                to={`?${stringify({
                  year: today.year,
                  month: today.month
                })}`}
                title={intl.formatMessage({
                  id: 'today',
                  defaultMessage: 'Aujourd\'hui'
                })}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faCalendarDay}
                />
              </Button>
              <Button
                component={AnchorLink}
                to={`?${stringify({
                  year: previousMonth.year,
                  month: previousMonth.month
                })}`}
                title={previousMonth.toLocaleString({
                  month: 'long',
                  year: 'numeric'
                })}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronLeft}
                />
              </Button>
              <Button
                component={AnchorLink}
                to={`?${stringify({
                  year: nextMonth.year,
                  month: nextMonth.month
                })}`}
                title={nextMonth.toLocaleString({
                  month: 'long',
                  year: 'numeric'
                })}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faChevronRight}
                />
              </Button>
              <Button
                disabled={eventsQuery.isFetching}
                loading={eventsQuery.isFetching}
                onClick={handleRefreshClick}
                title={intl.formatMessage({
                  id: 'refresh',
                  defaultMessage: 'Rafraîchir'
                })}
              >
                <FontAwesomeIcon
                  fixedWidth
                  icon={faRefresh}
                />
              </Button>
            </Toolbar>
            <Agenda
              dateTime={dateTime}
              events={events}
              style={{ flexGrow: 1 }}
            />
          </Stack>
        </Card>
      </Container>
    </Page>
  )
}

export default AgendaPage
